import { useEffect, useRef, useState } from "react";

import { naira } from "../pages/HomeShowcases";
import { Icon, IconName } from "./Icon";
import { prefersReducedMotion } from "./Phone";

// An illustration of the report page (report.hetheraengineering.com), with
// made-up numbers — not a real customer's report. It copies the real page's
// look (hethera-user-dashboard: ReportHero, Highlights, ChartCard): the dark
// hero with a greeting, the lime total and the waving buddy, a couple of
// "little wins" on pastel cards, then spending by bill type in the fixed
// category colours. When the real page's look changes, change this.

const TOTAL = 38350;

const WINS: { icon: IconName; tint: string; eyebrow: string; title: string; note: string }[] = [
  {
    icon: "phone",
    tint: "#e2edfb",
    eyebrow: "Your go-to",
    title: "Airtime",
    note: "38% of your spending",
  },
  {
    icon: "calendar",
    tint: "#ece6fb",
    eyebrow: "Favourite day",
    title: "Wednesdays",
    note: "You bought the most then",
  },
];

const BARS = [
  { label: "Airtime", share: 38, color: "#2a78d6" },
  { label: "Mobile data", share: 27, color: "#eb6834" },
  { label: "Cable TV", share: 21, color: "#1baf7a" },
  { label: "Electricity", share: 14, color: "#eda100" },
];

export function ReportMockup() {
  const ref = useRef<HTMLElement>(null);
  const total = useCountUpOnView(ref, TOTAL);

  return (
    <figure
      ref={ref}
      className="report-mock"
      aria-label="Example spending report page"
      style={{ margin: 0 }}
    >
      <div className="report-mock__hero">
        <div className="report-mock__top">
          <div>
            <span className="report-mock__hello">Good morning, Ada</span>
            <p className="report-mock__title">
              Here’s your September with Hethera
            </p>
          </div>
          <Buddy />
        </div>
        <p className="report-mock__label">You spent</p>
        <p className="report-mock__total" aria-label={naira(TOTAL)}>
          {naira(total)}
        </p>
        <p className="report-mock__meta">
          on 14 purchases
          <span className="report-mock__chip">▼ 8% vs August</span>
        </p>
      </div>

      <div className="report-mock__wins">
        {WINS.map((w, i) => (
          <div
            key={w.title}
            className="report-mock__win"
            style={{ background: w.tint }}
          >
            <span
              className="report-mock__sticker"
              style={{ transform: `rotate(${i % 2 ? 5 : -5}deg)` }}
            >
              <Icon name={w.icon} size={18} strokeWidth={2} />
            </span>
            <span className="report-mock__eyebrow">{w.eyebrow}</span>
            <strong>{w.title}</strong>
            <span className="report-mock__note">{w.note}</span>
          </div>
        ))}
      </div>

      <div className="report-mock__card">
        <div className="report-mock__card-head">
          <span className="report-mock__sticker" style={{ background: "#ece6fb" }}>
            <Icon name="chart" size={18} strokeWidth={2} />
          </span>
          <span>
            <strong>By bill type</strong>
            <small>What you spent on</small>
          </span>
        </div>
        <ul className="report-mock__bars">
          {BARS.map((b) => (
            <li key={b.label}>
              <span className="report-mock__name">{b.label}</span>
              <span className="report-mock__track">
                <span
                  style={{ width: `${b.share * 2.4}%`, background: b.color }}
                />
              </span>
              <span className="report-mock__pct">{b.share}%</span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

// The report's brand character: the app tile with lime eyes, waving.
function Buddy() {
  return (
    <svg
      className="report-mock__buddy"
      viewBox="0 0 120 120"
      width="58"
      height="58"
      overflow="visible"
      aria-hidden="true"
    >
      <rect
        x="8"
        y="10"
        width="92"
        height="92"
        rx="30"
        fill="#0e1a13"
        stroke="rgba(198, 241, 96, 0.5)"
        strokeWidth="2"
      />
      <path
        d="M24 26 q6 -7 16 -8"
        stroke="#c6f160"
        strokeOpacity="0.35"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <g fill="#c6f160">
        <ellipse cx="39" cy="44" rx="5.1" ry="6.9" />
        <ellipse cx="69" cy="44" rx="5.1" ry="6.9" />
        <circle cx="41" cy="41" r="1.8" fill="#0e1a13" />
        <circle cx="71" cy="41" r="1.8" fill="#0e1a13" />
      </g>
      <ellipse cx="30" cy="60" rx="7" ry="4.5" fill="#ff9fb2" opacity="0.55" />
      <ellipse cx="78" cy="60" rx="7" ry="4.5" fill="#ff9fb2" opacity="0.55" />
      <path
        d="M42 61 q12 13 24 0"
        stroke="#c6f160"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <g className="report-mock__hand">
        <rect
          x="98"
          y="26"
          width="16"
          height="26"
          rx="8"
          fill="#c6f160"
          stroke="#0e1a13"
          strokeWidth="3"
        />
      </g>
    </svg>
  );
}

// Prerendered with the final amount. If the preview is still below the fold,
// the total counts up from zero once, when it comes into view (like the real
// report); already scrolled past, or reduced motion, it just shows.
function useCountUpOnView(
  ref: React.RefObject<HTMLElement>,
  target: number,
  duration = 1100,
): number {
  const [value, setValue] = useState(target);
  useEffect(() => {
    const el = ref.current;
    if (
      !el ||
      prefersReducedMotion() ||
      el.getBoundingClientRect().top < window.innerHeight
    )
      return;
    setValue(0);
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setValue(t === 1 ? target : Math.round(target * eased));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      setValue(target);
    };
  }, [ref, target, duration]);
  return value;
}
